import {
  combineReducers,
  configureStore,
  type Reducer,
  type ThunkDispatch,
  type UnknownAction,
  type Dispatch,
} from "@reduxjs/toolkit";
import userSelectionSlice from "./slices/userSelection/userSelection";
import userDetailSlice from "./slices/userDetail/userDetailSlice";
import brandsSlice from "./slices/brands/brandsSlice";
import marketsSlice from "./slices/markets/marketsSlice";
import filtersSlice from "./slices/filters/filtersSlice";
import segmentsSlice from "./slices/segments/segmentsSlice";
import CONFIG from "@/config";
// JS assistant reducer from geo-ui (ported as-is); typed via JSDoc in
// chatReducer.js (ChatState + reducer signature) so no @ts-ignore is needed.
import chatReducer from "@/components/AIAssistant/state/chatReducer";
import featureStatusSlice from "./slices/featureStatus/featureStatusSlice";
import fileDownloadSlice from "./slices/fileDownload/fileDownloadSlice";
import bootstrapDataStatusSlice from "./slices/bootstrapDataStatus/bootstrapDataStatusSlice";
import brandsOrderMappingSlice from "./slices/brandsOrderMapping/brandsOrderMappingSlice";
import screenLevelSelectionSlice from "./slices/screenLevelSelection/screenLevelSelection";
import screenConfigSlice from "./slices/screenConfig/screenConfigSlice";
import pendingSelectionSlice from "./slices/pendingSelection/pendingSelectionSlice";
import dataReadinessSlice from "./slices/dataReadiness/dataReadinessSlice";
import { selectionListenerMiddleware } from "./listeners/selectionListener";
import { appliedSegmentsMirror } from "./middleware/appliedSegmentsMirror";
import { smartLinkReassert } from "./middleware/smartLinkReassert";
import distributionsCatalogSlice from "./slices/distributionsCatalog/distributionsCatalogSlice";
import questionsCatalogSlice from "./slices/questionsCatalog/questionsCatalogSlice";
import metricsCatalogSlice from "./slices/metricsCatalog/metricsCatalogSlice";
import smartLinkSlice from "./slices/smartLink/smartLinkSlice";
import viewsSlice from "./slices/views/viewsSlice";
import viewsV2Slice from "./slices/viewsV2/viewsV2Slice";
import categoriesSlice from "./slices/categories/categoriesSlice";
import aiModelsSlice from "./slices/aiModels/aiModelsSlice";
import humanMetricsSlice from "./slices/humanMetrics/humanMetricsSlice";

const activeGroupReducer: Reducer<{
  brands: ReturnType<typeof brandsSlice>;
  markets: ReturnType<typeof marketsSlice>;
  filters: ReturnType<typeof filtersSlice>;
  segments: ReturnType<typeof segmentsSlice>;
  bootstrapDataStatus: ReturnType<typeof bootstrapDataStatusSlice>;
  brandsOrderMapping: ReturnType<typeof brandsOrderMappingSlice>;
  distributionsCatalog: ReturnType<typeof distributionsCatalogSlice>;
  questionsCatalog: ReturnType<typeof questionsCatalogSlice>;
  metricsCatalog: ReturnType<typeof metricsCatalogSlice>;
  categories: ReturnType<typeof categoriesSlice>;
  aiModels: ReturnType<typeof aiModelsSlice>;
  humanMetrics: ReturnType<typeof humanMetricsSlice>;
}> = combineReducers({
  brands: brandsSlice,
  markets: marketsSlice,
  filters: filtersSlice,
  segments: segmentsSlice,
  bootstrapDataStatus: bootstrapDataStatusSlice,
  brandsOrderMapping: brandsOrderMappingSlice,
  distributionsCatalog: distributionsCatalogSlice,
  questionsCatalog: questionsCatalogSlice,
  metricsCatalog: metricsCatalogSlice,
  categories: categoriesSlice,
  aiModels: aiModelsSlice,
  humanMetrics: humanMetricsSlice,
});

export const rootReducer = {
  activeGroup: activeGroupReducer,
  featureStatus: featureStatusSlice,
  userSelection: userSelectionSlice,
  userDetail: userDetailSlice,
  fileDownload: fileDownloadSlice,
  screenLevelSelection: screenLevelSelectionSlice,
  screenConfig: screenConfigSlice,
  pendingSelection: pendingSelectionSlice,
  dataReadiness: dataReadinessSlice,
  smartLink: smartLinkSlice,
  views: viewsSlice,
  viewsV2: viewsV2Slice,
  // Assistant chat state (JS reducer, typed via JSDoc)
  chat: chatReducer,
};

export const store = configureStore({
  reducer: rootReducer,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .prepend(selectionListenerMiddleware.middleware as any)
      .prepend(appliedSegmentsMirror.middleware as any)
      .prepend(smartLinkReassert.middleware as any) as any,
  devTools: String(CONFIG.env) !== "production",
});

export type RootState = ReturnType<typeof store.getState>;
// Typed explicitly (rather than `typeof store.dispatch`) because inferring
// AppDispatch through the full reducer map's generic chain is fragile: once
// the legacy `chat` reducer carries a real (non-`never[]`) state type, TS's
// inference for configureStore's dispatch generic silently degrades to
// plain `Dispatch<UnknownAction>`, dropping thunk overloads store-wide.
export type AppDispatch = ThunkDispatch<RootState, undefined, UnknownAction> &
  Dispatch<UnknownAction>;
